import React, { useState, useEffect } from 'react';
import { X, Search, Check, Phone, Video, Loader2 } from 'lucide-react';
import { ref, get } from 'firebase/database';
import { db } from '../../../../integrations/firebase';

const GroupCallModal = ({ currentUser, isOpen, onClose, onStartCall }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchUsers();
            setSelectedUsers([]);
            setSearchTerm("");
        }
    }, [isOpen]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const snapshot = await get(ref(db, 'users'));
            if (snapshot.exists()) {
                const data = snapshot.val();
                const userList = Object.keys(data)
                    .filter(key => key !== currentUser.uid)
                    .map(key => ({
                        id: key,
                        ...data[key]
                    }));
                setAllUsers(userList);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    };

    const toggleUser = (user) => {
        if (selectedUsers.find(u => u.id === user.id)) {
            setSelectedUsers(prev => prev.filter(u => u.id !== user.id));
        } else {
            setSelectedUsers(prev => [...prev, user]);
        }
    };

    const handleStartCall = (type) => {
        if (selectedUsers.length === 0) return;
        if (selectedUsers.length > 1) return;

        // Extract IDs
        const targetIds = selectedUsers.map(u => u.id);
        onStartCall(targetIds, type);
        onClose();
    };

    if (!isOpen) return null;

    const filteredUsers = allUsers.filter(u =>
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl scale-100 animate-in zoom-in-95 duration-200 mx-4 flex flex-col max-h-[85vh]">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <Phone size={24} className="text-indigo-600" />
                        Start Call
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 bg-slate-100 rounded-full">
                        <X size={20} />
                    </button>
                </div>

                <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                    {/* Member Selection */}
                    <div className="flex-1 flex flex-col min-h-0">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Select Participants ({selectedUsers.length})</label>
                        <div className="relative mb-2">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
                                placeholder="Search people..."
                            />
                        </div>

                        <div className="flex-1 overflow-y-auto border border-slate-100 rounded-lg bg-slate-50 p-2 space-y-1 custom-scrollbar">
                            {loading ? (
                                <div className="flex justify-center p-4"><Loader2 className="animate-spin text-slate-400" /></div>
                            ) : filteredUsers.length === 0 ? (
                                <div className="text-center text-slate-400 text-sm p-4">No users found</div>
                            ) : (
                                filteredUsers.map(user => {
                                    const isSelected = !!selectedUsers.find(u => u.id === user.id);
                                    return (
                                        <div
                                            key={user.id}
                                            onClick={() => toggleUser(user)}
                                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-white border border-transparent'}`}
                                        >
                                            <div className={`w-5 h-5 rounded border flex items-center justify-center ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 bg-white'}`}>
                                                {isSelected && <Check size={12} className="text-white" />}
                                            </div>
                                            <img src={user.photo || `https://ui-avatars.com/api/?name=${user.name}`} className="w-8 h-8 rounded-full" alt="" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-800">{user.name}</p>
                                                <p className="text-xs text-slate-400 truncate">{user.email}</p>
                                            </div>
                                            <div className={`w-2 h-2 rounded-full ${user.status === 'online' ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {selectedUsers.length > 1 && (
                    <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm font-medium flex items-center gap-2">
                        ⚠️ Group calls coming soon. Select one person for a 1:1 call.
                    </div>
                )}

                <div className="mt-6 pt-4 border-t border-slate-100 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg font-medium">Cancel</button>

                    <button
                        onClick={() => handleStartCall('voice')}
                        disabled={selectedUsers.length === 0 || selectedUsers.length > 1}
                        className="bg-indigo-100 text-indigo-700 px-4 py-2 rounded-lg font-medium hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <Phone size={18} />
                        Voice
                    </button>

                    <button
                        onClick={() => handleStartCall('video')}
                        disabled={selectedUsers.length === 0 || selectedUsers.length > 1}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <Video size={18} />
                        Video
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GroupCallModal;
